/**
 * Kynward brand bridge — anchor meaning + light campfire/trail metaphor.
 * Use sparingly: marketing hero/brand section, early signup, and a few in-app moments.
 */

/** Canonical definition — marketing site + early signup. */
export const KYNWARD_BRAND_DEFINITION =
  'Kynward means moving toward your kin — one Circle at a time.';

export const KYNWARD_BRAND_DEFINITION_SHORT = 'Kynward means moving toward your kin.';

/** Recurring tagline (footer, trust strips). */
export const KYNWARD_BRAND_TAGLINE = 'Circles. Real connection. A calmer trail toward your kin.';

export const KYNWARD_BRAND_TAGLINE_SHORT = 'Circles. Real connection.';

/** Main positioning — marketing hero, welcome, final CTA. */
export const KYNWARD_MAIN_POSITIONING = 'Real connection from the very first conversation.';

/** Canonical expectation-setting line — signup, queue, Circle UI, Mutuals. */
export const KYNWARD_POSITIONING_ANCHOR = 'Start in a small Circle. Keep talking when it clicks.';

/** Marketing / hero support — broader than dating without overpromising it. */
export const KYNWARD_POSITIONING_MARKETING_SUPPORT =
  'Some connections become friendship. Some may become dating. Some are just a good conversation. What continues is always mutual.';

/** Event / mixer comparison — marketing and product explainers. */
export const KYNWARD_EVENT_COMPARISON =
  'Like a small social room, but with prompts, better pacing, and private Mutuals after.';

/** Dating expectation — signup, queue, preferences. */
export const KYNWARD_DATING_EXPECTATION = 'Dating can happen, but it is never assumed.';

/** Circle expectation — signup footer, in-Circle framing. */
export const KYNWARD_CIRCLE_EXPECTATION = 'Every Circle starts with conversation first.';

/** Mutuals expectation — wrap, marketing, Mutuals intro. */
export const KYNWARD_MUTUALS_EXPECTATION =
  'Mutuals are where friendship, dating, or either can continue when it lines up.';

/** Kynmark value — Circle board, marketing callouts. */
export const KYNWARD_KYNMARK_VALUE =
  'Kynmarks give people a light cue for how to reply, so starting the conversation feels less like guessing.';

/** Homepage / signup marketing promise. */
export const KYNWARD_MARKETING_PROMISE =
  'Kynward is a conversation-first way to find real connection nearby \u2014 before anyone has to perform, guess, or walk into a room cold.';

/**
 * Why conversation precedes friendship/dating labels — human, not academic.
 * Grounds the product without claiming “science proves Kynward is better than events.”
 */
export const KYNWARD_CONVERSATION_FIRST_ONELINER =
  'Before it becomes friendship or dating, it has to become a real conversation.';

/** Marketing site — after “How Kynward works,” before product detail. */
export const KYNWARD_MARKETING_CONVERSATION_FIRST = {
  h2: 'Connection starts before the label.',
  lead: KYNWARD_CONVERSATION_FIRST_ONELINER,
  paragraphs: [
    'Friendship and dating both need more than being in the same room or matching on a profile. They need context, a reason to open up, and a way to respond that feels natural.',
    'Kynward starts with shared prompts, small Circles, and reply cues because connection grows when people have context and something real to respond to. You get a few days of real conversation first \u2014 then choose what, if anything, continues.',
  ] as const,
  shortH2: 'Built around how connection actually starts.',
  shortBody:
    'Real connection usually needs context, openness, and responsive conversation. Kynward creates that first \u2014 before anyone has to decide if it is friendship, dating, or something in between.',
} as const;

/** Signup — before / near the “what are you open to” intent screen. */
export const KYNWARD_SIGNUP_CONVERSATION_FIRST = {
  title: 'Conversation comes first.',
  body: 'Your intent helps us place you, but every Circle starts the same way: with shared prompts and low-pressure conversation.',
  footer:
    'After the Circle, you privately choose what could continue \u2014 friendship, dating, either, or nothing this time.',
} as const;

/** Pre–queue join reassurance (signup finding circle). */
export const KYNWARD_QUEUE_JOIN_REASSURANCE = {
  title: 'Conversation first. Mutual after.',
  body: 'Your intent helps place you with people open to the same things. What continues after is always mutual.',
} as const;

/** In-Circle neutral framing — no intent labels during the group. */
export const KYNWARD_CIRCLE_NEUTRAL_NOTE = `${KYNWARD_CIRCLE_EXPECTATION} You\u2019ll choose what continues after the Circle.`;

/**
 * Wrap-up / Mutuals selection intro.
 *
 * Opened with "Choose what you'd be open to." \u2014 word for word the title rendered directly above it
 * (MUTUAL_CONTINUATION_CHOOSE_TITLE). This is the canonical home of the picks-privacy promise; the
 * lane picker and the wrap paywall both restated it within seconds and now defer here.
 */
export const KYNWARD_MUTUALS_CHOOSE_INTRO =
  'Your picks are private. A Mutual opens only when you both choose each other and your connection lanes line up.';

/**
 * Conversation settings — Campfire is the first setting today; do not imply others ship yet.
 *
 * **Marketing / store:** phone-first, prompt-led Circles; Campfire lives below the hero as a
 * “conversation setting,” not a physical social setting.
 *
 * **In-app product:** `signupCard*`, `circleHeader*`, `circleInfo*` — plain “setting” / “Campfire”
 * language only (Circle = the group; Campfire = the current setting).
 *
 * @see docs/store/APP_STORE_LISTING.md
 */
export const KYNWARD_SOCIAL_SETTINGS = {
  currentSettingName: 'Campfire',
  /** Homepage hero lead — phone-first; no Campfire or “rotating” here */
  heroSubLine:
    'Kynward brings nearby people into small, prompt-led Circles so conversation starts with context \u2014 not swipes, cold DMs, or walking into a room of strangers.',
  /** Marketing homepage hero — short above-the-fold lead */
  /**
   * Hero sub. Its job is the ARC — group, days, then the meet-up — not a mechanic list. The
   * previous version ("a small Circle of nearby people, one prompt everyone answers / you talk
   * first, then choose who continues") said almost exactly what `problemSection.bridge` says a
   * screen further down, so the reader met the same sentence twice, and it stopped before the
   * part that matters: actually meeting. Ending on the meet-up is deliberate — that beat was
   * invisible on this page until it got its own section.
   *
   * Say "match" plainly. "Then you choose who continues" was the in-house euphemism for it and read
   * as evasive to someone meeting the product cold. "Only if" is load-bearing and correct: mutual
   * choice is a NECESSARY condition, not a sufficient one — the connection lanes still have to line
   * up — so the sentence must never be flipped into "if you both choose, you match".
   *
   * End on "meet for real", not "the meetup is built in". The second is a feature; the first is
   * the outcome this audience says it wants — every current survey of app-tired singles and
   * friend-seekers has them preferring to end up in person — and it is the one thing a swipe app
   * cannot say. "The plan is built in" stays as the proof, after the outcome.
   */
  heroSubShort:
    'A few people near you, one thing to talk about, three days.\nYou match only if you both choose each other — then meet for real. The plan is built in.',
  heroSubLineAlt:
    'Small, prompt-led Circles with nearby people \u2014 conversation starts with context, not at a crowded event.',
  sectionHeadline: 'Inside each Circle',
  sectionBody:
    'Every Circle has a conversation setting that shapes the mood, prompts, and small activities for the next few days.\n\nCampfire is the first setting: warm, low-pressure, and built for easy first replies.',
  sectionBodyShort:
    'Each Circle has a conversation setting that shapes the mood and prompts. Campfire is first: warm, low-pressure, and built for easy first replies.',
  oneLiner: 'Small, prompt-led Circles — not blank group chats or endless feeds.',
  storeListingLine:
    'Meet nearby people through small, prompt-led Circles on your phone. Daily prompts, mutual kindles, no swiping.',
  /** Signup — immediate experience only; no roadmap */
  signupCardTitle: 'Your Circle opens in a setting',
  signupCardBody: 'Campfire is first: warm, low-pressure, and built for easy first replies.',
  /** Circle chat header */
  circleHeaderLabel: 'Setting: Campfire',
  circleHeaderLabelA11y: 'Campfire setting for this Circle. Learn more.',
  /** Circle info sheet — fuller explanation + optional footnote */
  circleInfoKicker: 'Setting',
  circleInfoTitle: 'Campfire',
  circleInfoBody:
    'Warm, low-pressure, and built around daily prompts, activities, and side conversations.',
  circleInfoFootnote:
    'Kynward settings can rotate over time, so future Circles may feel different while keeping the same small-group flow.',
} as const;

/** Marketing hero */
export const KYNWARD_MARKETING_HERO = {
  h1: 'Making friends shouldn’t be this hard.\nNeither should dating.',
  h1AccentLineIndex: 1,
  sub: KYNWARD_SOCIAL_SETTINGS.heroSubShort,
  /**
   * CADENCE claims the DROP, never your placement in one. "Circles form every evening" read as a
   * promise to the reader and the product does not make it: the in-app queue screen a member lands
   * on a minute later says "Most Circles form within 1-3 days", first-pass formation bails under a
   * pool of four, and leftovers roll to the next drop.
   *
   * PHOTO VERIFICATION stays by founder decision 2026-08-20. What the claim rests on, checked
   * against the deployed environment 2026-09-05 (not the repos — neither repo carries the value):
   * - Cloud Run `kynward-dev-api` has PROFILE_PICTURE_VISION_ENABLED=true. It is set as the HCP
   *   Terraform workspace variable `cloud_run_env` (the service's lastModifier is tfc-runner@),
   *   which is why grepping .env / cloudbuild / workflows finds nothing. The GitHub deploy runs
   *   `gcloud run deploy --image` with no env flags, so a redeploy keeps it.
   * - vision.googleapis.com is enabled on the project; the SA uses ADC, no IAM role (roles/
   *   vision.user is not valid at project scope — kynward-infrastructure cloud_vision.tf).
   * - The client initialises: every instance start logs "profile photo vision: enabled (Safe
   *   Search all photos; face required on primary only, min_conf=0.65)". Query with
   *   SEARCH("profile photo vision") — the lines are jsonPayload.msg, so textPayload filters miss.
   * - NOT yet observed there: an actual upload decision. Zero "profile_photo_vision: user_id=…
   *   decision=…" lines in 90 days, so the ADC → Vision call has only been proven to construct,
   *   not to annotate. One real profile-photo upload on the deployed dev app closes that.
   * - No prod Cloud Run service exists yet. The prod workspace must carry the same variable.
   * What "verified" means: the primary photo passed Safe Search and contains at least one face
   * at ≥0.65 confidence. It is not a selfie/liveness match — hence "profiles", never "people".
   * IDV_PROVIDER is unset everywhere; that path is dormant.
   * This line also ships into the native app first screen via WELCOME_DOOR_COPY, where it cannot be
   * hotfixed like a web deploy.
   *
   * "No open inbox", not "No cold DMs" — the DM claim was falsified by both Fireside surfaces and
   * dropped from the door heroes on 2026-08-20 (claims block above `marketingDoorHeroes` in
   * landingContent.ts); the default hero had kept it.
   */
  // "New Circles form nightly at 7pm" retired 2026-10-01 with the weekly-occasion story (a
  // visitor now picks one of four weekly nights, not a nightly drop) — see the claims block
  // above `marketingDoorHeroes` in landingContent.ts. This string has no live reader today (the
  // marketing-site home hero reads `marketingHomeHero`, not `marketingHero`/`KYNWARD_MARKETING_HERO`
  // — see the comment on `marketingHomeHero`) and `WELCOME_DOOR_COPY.trustLine` builds from
  // `marketingHero.proofChips`, not this string. Kept in sync anyway so a revived caller never sees
  // a retired claim.
  trust: 'No swiping. No open inbox. Photo-verified profiles.',
} as const;

export const KYNWARD_MARKETING_WHY_SECTION = {
  h2: 'Why Kynward?',
  lead: KYNWARD_BRAND_DEFINITION_SHORT,
  body: 'Every Circle is a shared space: a few people, one daily prompt, and enough room to show up without performing. From there, the flow is simple — talk for a few days, see who feels natural, and kindle the connections that feel mutual.',
  bodyShort:
    'We use Circles, campfires, and trails because meeting new people should feel warm, gradual, and low-pressure — not like performing on a profile or chasing matches.',
} as const;

export const KYNWARD_MARKETING_CAMPFIRE_SECTION = {
  h2: 'A Circle, not a crowded feed',
  paragraphs: [
    'Every Circle has a conversation setting that shapes the mood, prompts, and small activities for a few days — all on your phone first.',
    'Campfire is the first setting: warm, low-pressure, and built for easy first replies. When the interest feels mutual, you can kindle a Mutual after the Circle ends.',
  ] as const,
} as const;

/**
 * No live reader as of 2026-10-01 \u2014 `rhythmSection` (landingContent.ts) reads this but nothing
 * renders `rhythmSection`; `HowItWorksBand` reads `howItWorksSection` instead. Kept in sync with
 * the weekly-occasion story anyway so a revived caller never sees a retired claim.
 */
export const KYNWARD_MARKETING_TRAIL_SECTION = {
  h2: 'How Kynward works',
  lead: 'Circles are small, prompt-led groups that unfold over a few days. You do not have to swipe, cold DM, or walk into a room alone.',
  steps: [
    {
      title: 'Start in a small Circle',
      body: 'Pick a weekly night and a Circle of 4\u20136 nearby people who picked the same one forms a few days before it \u2014 you join the first your city can fill, and everyone starts in the same place.',
    },
    {
      title: 'Talk with context',
      body: 'Everyone gets the same prompt, light activities, and reply cues so conversation has somewhere to start.',
    },
    {
      title: 'Choose what continues',
      body: 'Every Circle ends at a meetup the group picks. Privately choose who you\u2019d be open to hearing from again \u2014 Mutuals open only when both people choose each other and the connection lane lines up.',
    },
  ] as const,
} as const;

/**
 * Marketing site — Circle visual walkthrough (after How it works). The day-by-day timeline and
 * "Day N of 3" preview badges below predate the weekly-occasion story and still imply a fixed
 * 3-day Circle; restructuring them is a content redesign out of scope for the 2026-10-01 copy
 * sweep (see retiredClaims coverage note). Only the heading's explicit "3-day" claim is retired
 * here.
 */
export const KYNWARD_MARKETING_CIRCLE_TIMELINE = {
  h2: 'Inside a Circle',
  subtitle:
    'A small group starts with one easy prompt, builds context through replies and activities, then wraps with private Mutuals. When the group has more to say, it can keep going.',
  timeline: [
    {
      day: 1,
      dayLabel: 'DAY 1',
      title: 'Start easy',
      body: 'One low-pressure prompt gets everyone into the room.',
    },
    {
      day: 2,
      dayLabel: 'DAY 2',
      title: 'Add context',
      body: 'Replies and activities help the group build on first answers.',
    },
    {
      day: 3,
      dayLabel: 'DAY 3',
      title: 'Notice what stuck',
      body: 'Before Wrap, people see what felt worth continuing.',
    },
  ] as const,
  previews: [
    {
      day: 1,
      dayBadge: 'Day 1 of 3',
      title: 'Tonight\u2019s Circle',
      status: '6 people nearby \u00b7 2/6 checked in',
      promptLabel: 'TODAY\u2019S PROMPT',
      prompt: 'What\u2019s one easy thing people could ask you about?',
      chips: ['Food or coffee', 'Current show', 'Local spot'] as const,
      helper: 'Small answer is fine.',
      emphasis: 'compact' as const,
    },
    {
      day: 2,
      dayBadge: 'Day 2 of 3',
      title: 'Add the next layer',
      status: '4/6 checked in',
      promptLabel: 'TODAY\u2019S PROMPT',
      prompt: 'What\u2019s the small story behind what you shared yesterday?',
      helper: 'Add the next layer, or ask someone about theirs.',
      activityPills: ['Common Ground Map', 'Build the Fire', 'Side Trail'] as const,
      emphasis: 'featured' as const,
    },
    {
      day: 3,
      dayBadge: 'Day 3 of 3',
      title: 'Before Wrap',
      status: 'Mutuals open after the Circle',
      promptLabel: 'TODAY\u2019S PROMPT',
      prompt: 'What\u2019s one answer from someone here that made you want to ask more?',
      helper: 'Notice what felt worth continuing.',
      footerPill: 'Private picks after Wrap',
      emphasis: 'compact' as const,
    },
  ] as const,
  featureChips: [
    { label: 'Prompt', micro: 'One shared question', glyph: 'prompt' as const },
    { label: 'Activities', micro: 'Shared context', glyph: 'activities' as const },
    { label: 'Side Trail', micro: 'Lighter threads', glyph: 'sideTrail' as const },
    { label: 'Kynmarks', micro: 'Reply cues', glyph: 'kynmarks' as const },
  ] as const,
  /**
   * Headings for the row of real app captures beneath the walkthrough.
   *
   * Deliberately not Day 1 / 2 / 3: the walkthrough directly above is already a day-by-day
   * timeline, and labelling the screenshots the same way would say the same thing twice. The split
   * that the days do not cover is what a Circle looks like from inside versus what you leave with.
   */
  shotGroups: [
    {
      id: 'inside' as const,
      label: 'Inside the Circle',
      body: 'One prompt everyone answers, replies that give you somewhere to start, and a lighter thread running alongside the main one.',
    },
    {
      id: 'wrap' as const,
      label: 'When it ends',
      body: 'You privately choose who you\u2019d keep talking to. A Mutual opens only where you both chose each other.',
    },
  ] as const,
} as const;

/** SignUp promise / welcome */
export const KYNWARD_SIGNUP_PROMISE = {
  openingLead: KYNWARD_MAIN_POSITIONING,
  openingBody:
    'You\u2019ll join small Circles where conversation starts with shared prompts and grows naturally over a few days.',
  calmerWay: KYNWARD_MARKETING_PROMISE,
} as const;

export const KYNWARD_SIGNUP_RULES_INTRO =
  'Circles work best when they feel warm, respectful, and low-pressure.';

/** Queue / forming */
export const KYNWARD_FORMING_CIRCLE = {
  headline: 'Your Circle is forming',
  body: 'This Circle will start in Campfire — a few people nearby, one shared prompt, and space to ease into conversation.',
} as const;

/** First Circle entry (reveal modal) — welcome + low-pressure nudge, not philosophy. */
export const KYNWARD_FIRST_CIRCLE_WELCOME = {
  eyebrow: 'YOU\u2019RE IN',
  title: 'Welcome to your Circle',
  lead: 'Start small. One reply is enough.',
  sharedGroundEyebrow: 'In Common',
  promptHintClosed: 'Today\u2019s prompt opens tonight.',
  ctaOpen: 'Start with today\u2019s prompt',
  ctaClosed: 'Go to Circle',
} as const;

/**
 * "You're here for Game night — Thursday · 7 PM." — the welcome reveal's occasion line,
 * shown only when the Circle formed around one. `schedule` is `circleOccasionScheduleLabel`'s
 * output ("Thursday · 7 PM"); empty when the title is missing.
 */
export function firstCircleOccasionLine(title?: string, schedule?: string): string {
  const t = title?.trim();
  const s = schedule?.trim();
  if (!t) return '';
  return s ? `You\u2019re here for ${t} \u2014 ${s}.` : `You\u2019re here for ${t}.`;
}

/** Hub detail — collapsed chip + expanded explainer for shared ground + overlap signals. */
export const CIRCLE_DETAIL_SHARED_GROUND_COPY = {
  chipFallbackLabel: 'Threads',
  chipAccessibilityHint: 'Learn what overlap means',
  tierExplainer:
    'A read on how much this Circle\u2019s preferences line up overall. It\u2019s a starting point — not a grade.',
  overlapExplainer: 'Each filled dot is a thread several of you share — interests, pace, or vibe.',
} as const;

/** Day 3 / wrap */
export const KYNWARD_WRAP_TRAIL = {
  headline: 'Follow the trail from here',
  body: KYNWARD_MUTUALS_CHOOSE_INTRO,
} as const;
